import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { session, user } = useAuth();

  useEffect(() => {
    // Skip socket connection for now to focus on core functionality
    // Socket.IO will be re-enabled when needed for real-time features
    if (false && session?.access_token && user?.id) {
      console.log('Attempting to connect to Socket.IO server...');
      
      // Remove /api from the URL since Socket.IO connects to the base server
      const serverUrl = process.env.REACT_APP_API_URL 
        ? process.env.REACT_APP_API_URL.replace('/api', '')
        : 'http://localhost:3001';
      
      console.log('Connecting to:', serverUrl);
      
      const newSocket = io(serverUrl, {
        auth: {
          token: session.access_token
        },
        transports: ['polling', 'websocket'],
        timeout: 5000,
        forceNew: true
      });

      newSocket.on('connect', () => {
        console.log('✅ Connected to Socket.IO server');
        setConnected(true);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('❌ Disconnected from server:', reason);
        setConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('🔴 Socket connection error:', error.message);
        setConnected(false);
      });

      setSocket(newSocket);

      return () => {
        console.log('🧹 Cleaning up socket connection');
        newSocket.disconnect();
        setSocket(null);
        setConnected(false);
      };
    } else {
      console.log('🔌 Socket.IO disabled - using polling for updates');
      setSocket(null);
      setConnected(false);
    }
  }, [session?.access_token, user?.id]);

  // Socket event handlers
  const joinJob = (jobId) => {
    if (socket && connected) {
      socket.emit('join-job', jobId);
    }
  };

  const leaveJob = (jobId) => {
    if (socket && connected) {
      socket.emit('leave-job', jobId);
    }
  };

  const startTyping = (jobId) => {
    if (socket && connected) {
      socket.emit('typing-start', { jobId });
    }
  };

  const stopTyping = (jobId) => {
    if (socket && connected) {
      socket.emit('typing-stop', { jobId });
    }
  };

  const emitJobStatusUpdate = (data) => {
    if (socket && connected) {
      socket.emit('job-status-update', data);
    }
  };

  const emitApplicationStatusUpdate = (data) => {
    if (socket && connected) {
      socket.emit('application-status-update', data);
    }
  };

  const emitPaymentStatusUpdate = (data) => {
    if (socket && connected) {
      socket.emit('payment-status-update', data);
    }
  };

  const emitNewRating = (data) => {
    if (socket && connected) {
      socket.emit('new-rating', data);
    }
  };

  const value = {
    socket,
    connected,
    joinJob,
    leaveJob,
    startTyping,
    stopTyping,
    emitJobStatusUpdate,
    emitApplicationStatusUpdate,
    emitPaymentStatusUpdate,
    emitNewRating
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}